import { useEffect, useRef, useState } from "react";
import { CalculatorInputs, RetentionType, RulesPack, VATType } from "../types";
import { calculateAll } from "../utils/calculator";

type ImportChoice = "scenario_b" | "replace";

interface ImportPayload {
  grossAnnual: number;
  costsAnnual: number;
  retentionType: RetentionType;
  vatType: VATType;
  targetMonthly: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  inputs: CalculatorInputs;
  rules: RulesPack;
  isPremium: boolean;
  onImport: (choice: ImportChoice, payload: ImportPayload) => void;
}

function formatEUR(value: number) {
  return value.toLocaleString("es-ES", { maximumFractionDigits: 0 });
}

export default function InverseCalculatorModal({
  isOpen,
  onClose,
  inputs,
  rules,
  isPremium,
  onImport,
}: Props) {
  const [targetMonthly, setTargetMonthly] = useState("");
  const [costsMonthly, setCostsMonthly] = useState("");
  const [isFirstYear, setIsFirstYear] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultGrossAnnual, setResultGrossAnnual] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importChoice, setImportChoice] = useState<ImportChoice>("scenario_b");
  const modalRef = useRef<HTMLDivElement | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setIsCalculating(false);
      setConfirmOpen(false);
      setIsFirstYear(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setImportChoice(isPremium ? "scenario_b" : "replace");
  }, [isOpen, isPremium]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (confirmOpen) {
          setConfirmOpen(false);
        } else {
          onClose();
        }
      }
      if (event.key === "Tab") {
        const root = confirmOpen ? confirmRef.current : modalRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (event.shiftKey && active === first) {
          last.focus();
          event.preventDefault();
        } else if (!event.shiftKey && active === last) {
          first.focus();
          event.preventDefault();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const root = confirmOpen ? confirmRef.current : modalRef.current;
    const firstFocusable = root?.querySelector<HTMLElement>(
      'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, confirmOpen, onClose]);

  const retentionType: RetentionType = "profesional_15";
  const vatType: VATType = "general_21";

  const buildInverseInputs = (grossAnnual: number, costsAnnual: number) => {
    const next = JSON.parse(JSON.stringify(inputs)) as CalculatorInputs;
    next.isTarifaPlana = isFirstYear;
    next.autoRETA = !isFirstYear;
    next.socialSecurityAnnual = isFirstYear ? 984.96 : inputs.socialSecurityAnnual;
    next.activities = [
      {
        concept: "Facturación objetivo",
        baseAmount: grossAnnual,
        vatType,
        retenType: retentionType,
        exempt: vatType === "exempt",
        frequency: "annual",
      },
    ];
    next.costs = costsAnnual > 0
      ? [
          {
            concept: "Costes estimados (inversa)",
            baseAmount: costsAnnual,
            hasVAT: false,
            deductiblePct: 100,
            frequency: "annual",
          },
        ]
      : [];
    if (retentionType === "profesional_7") {
      next.retenApplies7Pct = true;
    }
    return next;
  };

  const handleCalculate = () => {
    setError(null);
    const target = Number(targetMonthly);
    const costs = Number(costsMonthly);
    if (!target || target <= 0 || !costs || costs < 0) {
      setError("Introduce un objetivo y costes válidos.");
      return;
    }
    const targetAdjusted = target;
    const epsilon = 1;
    let min = 0;
    let max = 500000;
    let iterations = 0;
    let bestGross = null as number | null;
    let bestResult = null as any;

    setIsCalculating(true);

    while (iterations < 60 && max - min > epsilon) {
      const mid = (min + max) / 2;
      const testInputs = buildInverseInputs(mid, costs * 12);
      try {
        const result = calculateAll(testInputs, rules);
        const netMonthly = (result.netoFinalTrasRenta ?? result.netoFiscalAnual ?? 0) / 12;
        bestGross = mid;
        bestResult = result;
        if (Math.abs(netMonthly - targetAdjusted) <= epsilon) {
          break;
        }
        if (netMonthly < targetAdjusted) {
          min = mid;
        } else {
          max = mid;
        }
      } catch {
        min = mid;
      }
      iterations++;
    }

    if (bestGross === null || !bestResult) {
      setError("No pudimos calcular el bruto con estos datos. Ajusta los valores e inténtalo.");
      setIsCalculating(false);
      return;
    }

    setResultGrossAnnual(bestGross);
    setIsCalculating(false);
  };

  const handleCtaClick = () => {
    window._paq?.push(["trackEvent", "CTA", "click", "calcular"]);
    handleCalculate();
  };

  const handleImport = () => {
    if (resultGrossAnnual === null) return;
    const payload: ImportPayload = {
      grossAnnual: resultGrossAnnual,
      costsAnnual: Number(costsMonthly) * 12,
      retentionType,
      vatType,
      targetMonthly: Number(targetMonthly) || 0,
    };
    onImport(importChoice, payload);
    setConfirmOpen(false);
    onClose();
  };

  if (!isOpen) return null;

  const grossMonthly = resultGrossAnnual ? resultGrossAnnual / 12 : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Calculadora inversa"
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.4)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        zIndex: 1200,
      }}
      onClick={onClose}
    >
      <style>{`
        @media (max-width: 640px) {
          .inverse-modal {
            height: 100%;
            max-height: none !important;
            border-radius: 0 !important;
            width: 100% !important;
          }
        }
      `}</style>
      <div
        className="inverse-modal"
        style={{
          width: "100%",
          maxWidth: "680px",
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 20px 60px rgba(15, 23, 42, 0.2)",
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        ref={modalRef}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", marginBottom: "8px" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "var(--text)" }}>
              Calculadora inversa
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: "13px", color: "var(--muted)" }}>
              Dinos cuánto quieres llevarte al mes (después de costes e impuestos) y te decimos cuánto debes facturar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar calculadora inversa"
            style={{
              appearance: "none",
              border: "1px solid var(--border)",
              background: "white",
              borderRadius: "999px",
              width: "36px",
              height: "36px",
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginTop: "20px" }}>
          <div>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)" }}>
              Quiero cobrar al mes (€)
            </label>
            <input
              type="number"
              value={targetMonthly}
              onChange={(e) => setTargetMonthly(e.target.value)}
              style={{
                width: "100%",
                marginTop: "8px",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "2px solid var(--border)",
                fontSize: "14px",
              }}
              required
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text)" }}>
              Costes mensuales estimados (€)
            </label>
            <input
              type="number"
              value={costsMonthly}
              onChange={(e) => setCostsMonthly(e.target.value)}
              style={{
                width: "100%",
                marginTop: "8px",
                padding: "10px 12px",
                borderRadius: "10px",
                border: "2px solid var(--border)",
                fontSize: "14px",
              }}
              required
            />
          </div>
        </div>

        <div
          style={{
            marginTop: "16px",
            padding: "14px 16px",
            borderRadius: "12px",
            background: "var(--accent-soft)",
            border: "1px solid rgba(24,82,214,.12)",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isFirstYear}
              onChange={(e) => setIsFirstYear(e.target.checked)}
              style={{ marginTop: "2px" }}
            />
            <span style={{ fontSize: "12px", lineHeight: 1.5, color: "var(--text)" }}>
              <strong>Es mi primer año como autónomo</strong>
              <span style={{ display: "block", color: "var(--muted)", marginTop: "2px" }}>
                Aplicamos tarifa plana en la RETA para la estimación inversa.
              </span>
            </span>
          </label>
        </div>

        {error && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              borderRadius: "10px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: "13px",
            }}
          >
            {error}
          </div>
        )}

        <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleCtaClick}
            disabled={isCalculating}
            style={{
              padding: "12px 20px",
              borderRadius: "10px",
              border: "none",
              background: isCalculating ? "#9ca3af" : "var(--brand)",
              color: "white",
              fontWeight: 600,
              cursor: isCalculating ? "not-allowed" : "pointer",
            }}
          >
            {isCalculating ? "Calculando..." : "Calcular"}
          </button>
        </div>

        {resultGrossAnnual !== null && (
          <>
            <div
              style={{
                marginTop: "20px",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #bfdbfe",
                backgroundColor: "#eff6ff",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#1e40af" }}>
                Bruto a facturar
              </div>
              <div style={{ fontSize: "26px", fontWeight: 700, marginTop: "6px", color: "#1e3a8a" }}>
                {formatEUR(grossMonthly)} €/mes
              </div>
              <div style={{ fontSize: "13px", color: "#334155", marginTop: "4px" }}>
                {formatEUR(resultGrossAnnual)} €/año
              </div>
            </div>
          </>
        )}

        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "12px 18px",
              borderRadius: "10px",
              border: "1px solid var(--border)",
              background: "white",
              color: "var(--text)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cerrar
          </button>
          <button
            type="button"
            disabled={resultGrossAnnual === null}
            onClick={() => setConfirmOpen(true)}
            style={{
              padding: "12px 18px",
              borderRadius: "10px",
              border: "none",
              background: resultGrossAnnual === null ? "#9ca3af" : "var(--brand)",
              color: "white",
              fontWeight: 600,
              cursor: resultGrossAnnual === null ? "not-allowed" : "pointer",
            }}
          >
            Importar a la simulación
          </button>
        </div>

        {confirmOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
              zIndex: 1300,
            }}
              onClick={() => setConfirmOpen(false)}
            >
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                padding: "20px",
                maxWidth: "520px",
                width: "100%",
                  boxShadow: "0 20px 50px rgba(15, 23, 42, 0.25)",
                }}
                onClick={(e) => e.stopPropagation()}
                ref={confirmRef}
              >
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 700 }}>
                ¿Cómo quieres importar?
              </h3>
              <p style={{ margin: "6px 0 14px", fontSize: "13px", color: "var(--muted)" }}>
                Aplicar como Escenario B te permite comparar sin perder datos.
              </p>

              <label style={{ display: "flex", gap: "10px", marginBottom: "10px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="importChoice"
                  value="scenario_b"
                  checked={importChoice === "scenario_b"}
                  onChange={() => setImportChoice("scenario_b")}
                  disabled={!isPremium}
                />
                <span style={{ fontSize: "13px", color: !isPremium ? "var(--muted)" : "var(--text)" }}>
                  Aplicar como Escenario B (mantener mis datos actuales)
                </span>
              </label>
              {!isPremium && (
                <div style={{ fontSize: "12px", color: "#b45309", marginBottom: "10px" }}>
                  Disponible en Premium. Actívalo para comparar sin perder datos.
                </div>
              )}

              <label style={{ display: "flex", gap: "10px", marginBottom: "6px", cursor: "pointer" }}>
                <input
                  type="radio"
                  name="importChoice"
                  value="replace"
                  checked={importChoice === "replace"}
                  onChange={() => setImportChoice("replace")}
                />
                <span style={{ fontSize: "13px" }}>
                  Reemplazar datos actuales (sobrescribe Ingresos/Costes)
                </span>
              </label>
              {importChoice === "replace" && (
                <div style={{ fontSize: "12px", color: "#b91c1c", marginBottom: "12px" }}>
                  Esto sobrescribe tus ingresos y costes actuales. Recomendamos usar Escenario B.
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setConfirmOpen(false)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    border: "1px solid var(--border)",
                    background: "white",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleImport}
                  style={{
                    padding: "8px 14px",
                    borderRadius: "8px",
                    border: "none",
                    background: "var(--brand)",
                    color: "white",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Importar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
